/**
 * Due to the potential performance impacts of storing this state in redux, as well as the fact that
 * we don't require react re-rendering based on this state (at this time) I'm using a singleton here.
 *
 * Its intention at this time is to prevent element measurements during a zoom operation
 * - see measureElementDecorator.js
 *
 * If we end up needing things to re-render via react then we should revisit this.
 *
 * A second use case is now the zoom toast:
 * - It needs to display the zoom level immediately when the zoom changes, without waiting for a redux update
 */

export type ScaleOptions = {
    // If true, disables haptic feedback and toast display on zoom
    disableZoomFeedback: boolean;
};

type ZoomSubscriberCallback = (zoom: number, scaleOptions: ScaleOptions) => void;

const createZoomState = () => {
    let isZooming = false;

    // A list of subscriber functions that will be invoked when zoom finishes
    let zoomEndSubscribers: Set<() => void> = new Set();

    /**
     * Is a zoom operation in progress.
     */
    const getIsZooming = (): boolean => isZooming;

    /**
     * Sets whether a zoom operation is in progress.
     */
    const setIsZooming = (isNowZooming: boolean) => {
        isZooming = isNowZooming;

        // If we've finished zooming, invoke each subscriber callback
        if (!isNowZooming) {
            zoomEndSubscribers.forEach((subscriberCallback) => {
                subscriberCallback();
            });
            zoomEndSubscribers = new Set();
        }
    };

    /**
     * Registers a callback function that will be invoked when we finish zooming.
     *
     * This is currently used in the measureElementDecorator.js to allow elements to
     * be re-measured when a zoom has finished, if the element has not been measured yet.
     */
    const registerZoomEndSubscriber = (subscriberCallback: () => void) => {
        zoomEndSubscribers.add(subscriberCallback);
    };

    // A list of subscriber functions that will be invoked when the zoom changes
    const zoomSubscribers: Set<ZoomSubscriberCallback> = new Set();

    const registerZoomSubscriber = (subscriberCallback: ZoomSubscriberCallback) => {
        zoomSubscribers.add(subscriberCallback);
    };

    const unregisterZoomSubscriber = (subscriberCallback: ZoomSubscriberCallback) => {
        zoomSubscribers.delete(subscriberCallback);
    };

    const setZoomValue = (zoom: number, scaleOptions: ScaleOptions) => {
        zoomSubscribers.forEach((subscriberCallback) => {
            subscriberCallback(zoom, scaleOptions);
        });
    };

    return {
        // Zoom status
        getIsZooming,
        setIsZooming,
        registerZoomEndSubscriber,

        // Zoom value
        setZoomValue,
        registerZoomSubscriber,
        unregisterZoomSubscriber,
    };
};

const zoomState = createZoomState();

export default zoomState;
